/**
 * SupervisionSessionWidget — v10.2.4
 *
 * Widget flotante en el header del alumno durante una simulación supervisada.
 * Muestra en tiempo real:
 *  - Quién está conectado (instructores)
 *  - Quién tiene el control (badge naranja)
 *  - Botón "Solicitar control / atención del instructor"
 *
 * Diseño: dropdown discreto en el header, expandible en un panel compacto.
 * No bloquea clics (pointer-events-auto sólo en la píldora principal).
 *
 * Uso:
 *   <SupervisionSessionWidget
 *      supervisors={supervisors}
 *      isBeingControlled={isBeingControlled}
 *      controllerName={controllerName}
 *      onRequestControl={requestControl}
 *      wsConnected={wsConnected}
 *   />
 */
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Eye, Hand, Radio, CircleAlert, ChevronDown, ChevronUp, X } from 'lucide-react';
import { toast } from 'sonner';

export const SupervisionSessionWidget = ({
  supervisors = [],
  isBeingControlled = false,
  controllerName = null,
  onRequestControl,
  wsConnected = false,
}) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [showRequestBox, setShowRequestBox] = useState(false);
  const [lastRequestAt, setLastRequestAt] = useState(0);
  const [cooldownLeft, setCooldownLeft] = useState(0);

  const COOLDOWN_MS = 30 * 1000; // 30s entre solicitudes

  useEffect(() => {
    if (!lastRequestAt) return undefined;
    const iv = setInterval(() => {
      const left = Math.max(0, COOLDOWN_MS - (Date.now() - lastRequestAt));
      setCooldownLeft(left);
      if (left === 0) clearInterval(iv);
    }, 500);
    return () => clearInterval(iv);
  }, [lastRequestAt]);

  const handleRequest = () => {
    if (!onRequestControl) return;
    if (cooldownLeft > 0) return;
    const ok = onRequestControl(reason.trim());
    if (ok) {
      toast.success('Solicitud enviada al instructor', {
        icon: <Hand className="w-4 h-4" />,
        duration: 4000,
      });
      setLastRequestAt(Date.now());
      setReason('');
      setShowRequestBox(false);
    } else {
      toast.error('No hay conexión con el servidor');
    }
  };

  if (!wsConnected && supervisors.length === 0 && !isBeingControlled) {
    // Sin sesión de supervisión activa — no mostrar nada
    return null;
  }

  const activeCount = supervisors.length;
  const pillLabel = isBeingControlled
    ? 'Control del instructor'
    : activeCount === 1
    ? `${supervisors[0].user_name || 'Instructor'} observa`
    : activeCount > 1
    ? `${activeCount} instructores`
    : wsConnected
    ? 'Sala abierta'
    : 'Sin conexión';

  const pillColor = isBeingControlled
    ? 'bg-orange-500/15 border-orange-400/60 text-orange-200 hover:bg-orange-500/25'
    : activeCount > 0
    ? 'bg-blue-500/15 border-blue-400/60 text-blue-200 hover:bg-blue-500/25'
    : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600';

  return (
    <div className="relative" data-testid="supervision-session-widget">
      {/* Pill compacta en el header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${pillColor}`}
        data-testid="supervision-widget-toggle"
        title="Ver sesión de supervisión"
      >
        <span className="relative flex h-2 w-2">
          <span
            className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${
              wsConnected ? 'animate-ping' : ''
            } ${isBeingControlled ? 'bg-orange-400' : activeCount > 0 ? 'bg-green-400' : 'bg-slate-400'}`}
          />
          <span
            className={`relative inline-flex rounded-full h-2 w-2 ${
              isBeingControlled ? 'bg-orange-500' : activeCount > 0 ? 'bg-green-500' : 'bg-slate-400'
            }`}
          />
        </span>
        {isBeingControlled ? (
          <Radio className="w-3.5 h-3.5" />
        ) : (
          <Eye className="w-3.5 h-3.5" />
        )}
        <span className="truncate max-w-[140px]">{pillLabel}</span>
        {open ? (
          <ChevronUp className="w-3.5 h-3.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Panel expandible */}
      {open && (
        <Card
          className="absolute top-full right-0 mt-2 w-80 shadow-2xl bg-slate-900/98 border-slate-700 text-slate-100 z-50 pointer-events-auto backdrop-blur"
          data-testid="supervision-widget-panel"
        >
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                  <Eye className="w-4 h-4 text-blue-300" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Sesión de supervisión</p>
                  <p className="text-[11px] text-slate-400">
                    {wsConnected ? 'Canal en vivo' : 'Reconectando…'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-slate-400 hover:bg-slate-800 hover:text-white"
                onClick={() => setOpen(false)}
                data-testid="supervision-widget-close"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Control status */}
            {isBeingControlled ? (
              <div
                className="rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2"
                data-testid="widget-control-status"
              >
                <p className="text-[11px] uppercase tracking-wide text-orange-300">
                  Control activo
                </p>
                <p className="text-sm font-medium text-orange-100">
                  {controllerName || 'Instructor'} está pilotando la simulación
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">
                  Control
                </p>
                <p className="text-sm text-slate-200">
                  Tienes control total de la simulación
                </p>
              </div>
            )}

            {/* Lista de conectados */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">
                Conectados ({activeCount})
              </p>
              {activeCount === 0 ? (
                <p className="text-xs text-slate-500 italic">
                  Aún no hay instructores observando esta sesión.
                </p>
              ) : (
                <ul className="space-y-1.5" data-testid="widget-supervisors-list">
                  {supervisors.map((s) => (
                    <li
                      key={s.user_id}
                      className="flex items-center gap-2 rounded-md bg-slate-800/70 px-2 py-1.5"
                    >
                      <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-semibold text-slate-200">
                        {(s.user_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate text-slate-100">
                          {s.user_name || 'Instructor'}
                        </p>
                        <p className="text-[10px] text-slate-400 truncate">
                          {s.mode === 'control_total'
                            ? 'Con control'
                            : s.mode === 'asistido'
                            ? 'Modo asistido'
                            : 'Observador'}
                        </p>
                      </div>
                      {s.mode === 'control_total' && (
                        <Radio className="w-3.5 h-3.5 text-orange-400" />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Solicitar control / atención */}
            {activeCount > 0 && (
              <div className="pt-2 border-t border-slate-700">
                {!showRequestBox ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-center gap-2 border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 hover:text-white"
                    onClick={() => setShowRequestBox(true)}
                    disabled={cooldownLeft > 0}
                    data-testid="widget-request-control-btn"
                  >
                    <Hand className="w-3.5 h-3.5" />
                    {cooldownLeft > 0
                      ? `Espera ${Math.ceil(cooldownLeft / 1000)}s`
                      : isBeingControlled
                      ? 'Solicitar recuperar control'
                      : 'Solicitar atención del instructor'}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={
                        isBeingControlled
                          ? '¿Por qué necesitas recuperar el control?'
                          : '¿Qué necesitas del instructor? (opcional)'
                      }
                      className="min-h-[70px] bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500 text-xs"
                      maxLength={280}
                      data-testid="widget-request-reason"
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 text-slate-300 hover:bg-slate-800 hover:text-white"
                        onClick={() => {
                          setShowRequestBox(false);
                          setReason('');
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white gap-1.5"
                        onClick={handleRequest}
                        disabled={cooldownLeft > 0}
                        data-testid="widget-request-send-btn"
                      >
                        <Hand className="w-3.5 h-3.5" />
                        Enviar
                      </Button>
                    </div>
                  </div>
                )}
                <p className="mt-2 text-[10px] text-slate-500 leading-snug flex items-start gap-1">
                  <CircleAlert className="w-3 h-3 mt-0.5 shrink-0" />
                  El instructor recibirá una notificación con tu mensaje y
                  podrá liberar el control o intervenir.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SupervisionSessionWidget;
